import { useMemo } from "react";
import { usePermissions } from "@/modules/auth/presentation/model/usePermissions";
import { NOTIFICATION_BELL_PERMISSIONS } from "@/modules/notifications/domain/repo/notification-summary";
import { useNotificationSummaryQuery } from "@/modules/notifications/presentation/api/notifications-queries";
import { sidebarData } from "./data/sidebar-data";
import type { NavCollapsible, NavItem, SidebarData } from "./types";

function formatBadgeCount(count: number): string {
  return count > 99 ? "99+" : String(count);
}

function applyBadgeToItem(
  item: NavItem,
  badges: Record<string, number>,
): NavItem {
  if ("url" in item && item.url) {
    const count = badges[item.url];
    if (!count) return item;
    return { ...item, badge: formatBadgeCount(count) };
  }

  if ("items" in item && item.items) {
    const subItems = item.items.map((sub) => {
      const count = badges[sub.url];
      return count ? { ...sub, badge: formatBadgeCount(count) } : sub;
    });
    const total = subItems.reduce(
      (sum, sub) => sum + (badges[sub.url] ?? 0),
      0,
    );
    return {
      ...item,
      items: subItems,
      badge: total > 0 ? formatBadgeCount(total) : item.badge,
    } satisfies NavCollapsible;
  }

  return item;
}

function applySidebarBadges(
  data: SidebarData,
  badges: Record<string, number>,
): SidebarData {
  if (Object.keys(badges).length === 0) return data;

  return {
    ...data,
    navGroups: data.navGroups.map((group) => ({
      ...group,
      items: group.items.map((item) => applyBadgeToItem(item, badges)),
    })),
  };
}

export function useSidebarNavigation(): SidebarData {
  const { hasAny } = usePermissions();
  const canPollBadges = hasAny(NOTIFICATION_BELL_PERMISSIONS);
  const { data } = useNotificationSummaryQuery(canPollBadges);

  return useMemo(
    () => applySidebarBadges(sidebarData, data?.sidebarBadges ?? {}),
    [data?.sidebarBadges],
  );
}
